<template>
  <div class="container">
    <!-- Filters -->
    <div class="filter-container">
      <!-- Price Valid From -->
      <div class="filter-item">
        <label for="validFromDatePicker">Price Valid From</label>
        <input
            type="date"
            v-model="validFromDate"
            class="search-input"
            @change="handleDateChange"
        />
      </div>

      <!-- Status Filter -->
      <div class="filter-item">
        <label for="status">Status</label>
        <select v-model="selectedStatus" @change="updateStatus">
          <option value="">All</option>
          <option value="0">Pending</option>
          <option value="1">Approved</option>
          <option value="2">Rejected</option>
        </select>
      </div>

      <!-- Search Button -->
      <div class="filter-item">
        <label style="visibility: hidden;">Search</label>
        <button @click="searchItemPrices()" class="btn button-default">Search</button>
      </div>
    </div>

    <!-- Show Loading Spinner -->
    <div v-if="loading1" class="loading-spinner mt-3">
      <i class="fa fa-spinner fa-spin"></i> Loading...
    </div>

    <!-- Show Data Table -->
    <table v-if="!loading1 && itemPriceUpdates.length > 0" class="main-table">
      <thead>
      <tr>
        <th>Item Name</th>
        <th>Creation Details</th>
        <th>Confirmation Details</th>
        <th>Status</th>
        <th>Actions</th>
      </tr>
      </thead>

      <tbody>
      <tr v-for="(itemPrice, index) in itemPriceUpdates" :key="index">
        <td>{{ itemPrice.item_name }}</td>
        <td>
          <div>{{ itemPrice.created_by }}</div>
          <small class="text-muted" style="color: #6c757d; font-size: 0.85em;">{{ formatDateTime(itemPrice.created_at) }}</small>
        </td>
        <td>
          <div v-if="itemPrice.status === '1' || itemPrice.status === '2'">
            <div>{{ itemPrice.confirmed_by }}</div>
            <small class="text-muted" style="color: #6c757d; font-size: 0.85em;">{{ formatDateTime(itemPrice.confirmed_at) }}</small>
          </div>
          <span v-else>N/A</span>
        </td>
        <td>
            <span :class="statusClass(itemPrice.status).class">
              {{ statusClass(itemPrice.status).text }}
            </span>
        </td>
        <td>
          <button class="icon-button" @click="showPopup(itemPrice.id);">
            <span v-if="loading2" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
            <i v-else class="fa fa-eye"></i>
          </button>
        </td>
      </tr>
      </tbody>
    </table>

    <!-- Item Prices Modal Component -->
    <ItemPricesModal
        v-if="showModal"
        :itemPriceId="selectedItemPriceId"
        :allowApprove="true"
        @close="closePopup"
        @refresh="searchItemPrices"
    />

    <!-- Show message when data not found for given criteria -->
    <div v-if="!loading1 && itemPriceUpdates.length === 0">
      <img src="../../../../assets/vectors/data_not_found.png" alt="No Item Prices" class="error-image" />
      <p class="text-center error-message">Results not found!</p>
    </div>
  </div>
</template>

<script>
import {searchItemPrices} from "@/services/dataService";
import ItemPricesModal from "@/pages/Modal/itemPrices.vue";

export default {
  components: {
    ItemPricesModal
  },

  data() {
    // Get the current date
    const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    return {
      // Get the API access token from the local storage
      token: localStorage.getItem('token_set'),

      // Get the user profile id from the local storage
      profileId: localStorage.getItem('profile_id'),

      // Filter Item Prices by valid from date
      validFromDate: currentDate,

      // Filter Item Prices by Status (0=Pending, 1=Approved, 2=Rejected)
      selectedStatus: "",

      // Controls visibility of the loading until indicate item price history show in the grid view
      loading1: false,

      // Controls visibility of the loading until indicate item price data on the pop-up
      loading2: false,

      // Controls visibility of the popup modal for view item prices against district
      showModal: false,

      // Initialize the item prices array to fetch item prices from API
      itemPriceUpdates: [],

      // Initialize the selected item price ID
      selectedItemPriceId: '',
    }
  },

  watch: {
    // Disable the scroll when popup modal is visible times.
    showModal(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  },

  mounted() {
    // Call searchItemPrices function here
    this.searchItemPrices();
  },

  methods: {
    // Handle date change
    handleDateChange() {
      // Optional: Add any additional logic when date changes
    },

    // Update status filter
    updateStatus() {
      // Optional: Add any additional logic when status changes
    },

    // Show item price updated
    async searchItemPrices() {
      try {
        // Set Loading Status
        this.loading1 = true;

        // Reset Array
        this.itemPriceUpdates = [];

        // Construct the payload
        const payload = {
          valid_from: this.validFromDate,
          status: this.selectedStatus
        };

        // Call API and Fetch Data
        const response = await searchItemPrices(payload, this.token);
        if (response.data.status === "success") {
          this.itemPriceUpdates = response.data.data.map(itemPrice => ({
            id: itemPrice.id,
            item_name: itemPrice.item_name,
            created_at: itemPrice.created_at,
            created_by: itemPrice.created_by,
            confirmed_at: itemPrice.confirmed_at,
            confirmed_by: itemPrice.confirmed_by,
            status: itemPrice.status
          }));
        } else {
          console.log(response.data.message);
        }
      } catch (e) {
        console.error("Failed to fetch item price updates.");
      } finally {
        this.loading1 = false;
      }
    },

    // Show status badge
    statusClass(status) {
      const statusMap = {
        0: { text: "Pending", class: "badge badge-pending" }, // Orange
        1: { text: "Approved", class: "badge badge-approved" }, // Green
        2: { text: "Rejected", class: "badge badge-rejected" } // Red
      };

      return statusMap[status] || { text: "Unknown", class: "badge badge-secondary" }; // Default fallback
    },

    // Format DateTime as "YYYY-MM-DD | HH:MM:SS AM/PM"
    formatDateTime(dateTime) {
      if (!dateTime) return "N/A"; // Handle empty values

      const date = new Date(dateTime);
      if (isNaN(date.getTime())) return "Invalid Date"; // Handle invalid dates

      const options = { year: "numeric", month: "2-digit", day: "2-digit" };
      const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true };

      const formattedDate = date.toLocaleDateString("en-CA", options).replace(/\//g, "-"); // Ensures YYYY-MM-DD
      const formattedTime = date.toLocaleTimeString("en-US", timeOptions);

      return `${formattedDate} | ${formattedTime}`;
    },

    // Show Popup
    showPopup(itemPriceId) {
      this.selectedItemPriceId = itemPriceId;
      this.showModal = true;
    },

    // Close Popup
    closePopup() {
      this.showModal = false;
      this.selectedItemPriceId = '';
      this.searchItemPrices();
    },
  },
}
</script>

<style scoped>
/* Ensure the body and html take the full height of the page */
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  background-color: #f0f0f0; /* Background color for the page */
}

/* Container for the entire section */
.container {
  background-color: #ffffff;
  box-shadow: 0 0 15px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 20px;
  margin: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* Center items vertically */
  height: 100%; /* Take full height of the page */
  width: 100%; /* Take full width of the page */
  box-sizing: border-box; /* Ensure padding doesn't affect width */
}

/* Container for filter */
.filter-container {
  display: flex;
  flex-wrap: wrap;
  gap: 15px; /* Adjust spacing between items */
  width: 100%; /* Ensure container takes full width */
  margin-bottom: 20px; /* Space between filtering row and table */
}

/* Each filter item with label */
.filter-item {
  flex: 1;
  min-width: 180px; /* Ensure items take minimum reasonable width */
}

/* Force standard height and width to prevent giant stretching */
.filter-item input,
.filter-item select,
.filter-item button {
  width: 100% !important;
  height: 40px !important;
  box-sizing: border-box;
}

/* Styling for labels */
label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  font-size: 14px; /* Adjust font size for better readability */
}

/* Styling for date-picker */
input[type="date"] {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  height: 40px;
}

/* Styling for dropdown */
select {
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  width: 100%;
  height: 40px;
}

/* Button default styling */
.btn.button-default {
  padding: 10px 20px;
  background-color: #5A6166;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  width: 100%;
  height: 40px;
  transition: background-color 0.3s;
}

.btn.button-default:hover {
  background-color: #4a5055;
}

/* Loading spinner styles */
.loading-spinner {
  font-size: 18px;
  text-align: center;
  margin-top: 20px;
  color: #007bff; /* Preserved color for spinner */
}

/* Table */
.main-table {
  width: 100%;
  overflow-x: auto;
}
.main-table table {
  width: 100%;
  min-width: 800px; /* ensure scroll if too many cols */
  border-collapse: collapse;
  margin: 10px 0;
  border: none; /* remove table border */
}

/* Table Header */
.main-table th {
  border: none;
}
.main-table th {
  text-align: left;
  border-bottom: 2px solid #ddd; /* subtle bottom border for header */
  background-color: #f4f4f4;
  padding: 10px;
  white-space: nowrap;
}

/* Table Data */
.main-table td {
  text-align: left;
  border: none; /* remove individual cell borders */
  padding: 10px;
  white-space: nowrap;
  border-bottom: 1px solid #eee; /* row separator */
}

/* Optional: remove last row separator for cleaner look */
.main-table tbody tr:last-child td {
  border-bottom: none;
}

/* Styling for icon button */
.icon-button {
  margin: 0;
  font-size: 15px;
  background-color: transparent;
  border: none;
}
.fa-eye {
  cursor: pointer;
  transition: color 0.3s, transform 0.3s;
  margin-left: 5px;
}
.fa-eye:hover {
  color: #0056b3;
  transform: scale(1.2);
}
.fa-eye:active {
  transform: scale(1.1);
}

/* Badge Base Styles */
.badge {
  display: inline-block;
  padding: 5px 10px;
  border-radius: 3px;
  font-size: 12px;
  color: white;
  text-align: center;
  min-width: 75px;
}

/* Pending Badge */
.badge-pending {
  background-color: #ff9800; /* Orange */
}

/* Approved Badge */
.badge-approved {
  background-color: #28a745; /* Green */
}

/* Rejected Badge */
.badge-rejected {
  background-color: #f00; /* Red */
}

/* Secondary Badge (Default Fallback) */
.badge-secondary {
  background-color: #6c757d; /* Gray */
}

/* Results not found image */
.error-image {
  max-width: 250px;
}

/* Results not found message */
.error-message {
  font-size: 25px;
  font-weight: bold;
  color: #ccc;
  margin-top: -25px;
}
</style>